import { useCallback, useEffect, useState } from 'react'

import { useRouteMatch } from 'react-router'

import { type MarketoServiceError } from '@webapp/core/services/Marketo'
import { useAppRoutes } from '@webapp/hooks'
import {
  DISCOVER_MORE_EVENTS,
  DISCOVER_MORE_EXPERIMENT,
  DISCOVER_MORE_SURFACE,
} from '@webapp/platform/discover-more/constants'
import {
  type DiscoverMorePageParams,
  type DiscoverMorePageTab,
  DiscoverMorePageTabs,
} from '@webapp/platform/discover-more/pages'
import { DISCOVER_MORE_FEATURE } from './constants'
import { useDiscoverMoreFeatureContent } from './useDiscoverMoreFeatureContent'

export const useDiscoverMoreView = () => {
  const { url } = useAppRoutes()
  const { params } = useRouteMatch<DiscoverMorePageParams>()

  // Partial: only the tabs that offer a demo have a button to disable.
  const [buttonState, setButtonState] = useState<
    Partial<Record<DiscoverMorePageTab, boolean>>
  >({
    [DiscoverMorePageTabs.VENDOR_PORTAL]: false,
    [DiscoverMorePageTabs.GUIDED_INTAKE]: false,
    [DiscoverMorePageTabs.AP]: false,
    [DiscoverMorePageTabs.CONTRACTS]: false,
    [DiscoverMorePageTabs.CUSTOM_FIELDS]: false,
  })

  const activeTab = params.tab ?? DiscoverMorePageTabs.WHATS_NEW

  const isWhatsNewTab = activeTab === DiscoverMorePageTabs.WHATS_NEW

  const featureContent = useDiscoverMoreFeatureContent(activeTab)

  const feature = DISCOVER_MORE_FEATURE[activeTab]

  useEffect(() => {
    window.analytics?.track(DISCOVER_MORE_EVENTS.PAGE_OPENED, {
      surface: DISCOVER_MORE_SURFACE,
      experiment: DISCOVER_MORE_EXPERIMENT,
    })
  }, [])

  useEffect(() => {
    // No `feature` means the What's new tab, which reports its own view event
    // from its feature slice, where the release count is known.
    if (!feature) {
      return
    }

    window.analytics?.track(DISCOVER_MORE_EVENTS.PROMO_VIEWED, {
      feature,
      surface: DISCOVER_MORE_SURFACE,
      experiment: DISCOVER_MORE_EXPERIMENT,
    })
    // Fire on each tab activation (incl. the default tab on mount). `feature` is
    // derived from `activeTab`, so keying on the tab avoids duplicate fires.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  const onDemoRequested = useCallback(() => {
    setButtonState((prev) => ({ ...prev, [activeTab]: true }))
    window.analytics?.track(DISCOVER_MORE_EVENTS.DEMO_REQUESTED, {
      feature,
      surface: DISCOVER_MORE_SURFACE,
      experiment: DISCOVER_MORE_EXPERIMENT,
    })
  }, [feature, activeTab])

  const onDemoRequestFailed = useCallback(
    (error: MarketoServiceError) => {
      window.analytics?.track(DISCOVER_MORE_EVENTS.DEMO_REQUEST_FAILED, {
        feature,
        surface: DISCOVER_MORE_SURFACE,
        experiment: DISCOVER_MORE_EXPERIMENT,
        error: error?.message,
      })
    },
    [feature]
  )

  const onTourClicked = useCallback(() => {
    window.analytics?.track(DISCOVER_MORE_EVENTS.TOUR_CLICKED, {
      feature,
      surface: DISCOVER_MORE_SURFACE,
      experiment: DISCOVER_MORE_EXPERIMENT,
    })
  }, [feature])

  return {
    data: {
      featureContent,
      urls: {
        whatsNewUrl: url('DiscoverMorePage', {
          tab: DiscoverMorePageTabs.WHATS_NEW,
        }),
        vendorPortalUrl: url('DiscoverMorePage', {
          tab: DiscoverMorePageTabs.VENDOR_PORTAL,
        }),
        guidedIntakeUrl: url('DiscoverMorePage', {
          tab: DiscoverMorePageTabs.GUIDED_INTAKE,
        }),
        apUrl: url('DiscoverMorePage', {
          tab: DiscoverMorePageTabs.AP,
        }),
        contractsUrl: url('DiscoverMorePage', {
          tab: DiscoverMorePageTabs.CONTRACTS,
        }),
        customFieldsUrl: url('DiscoverMorePage', {
          tab: DiscoverMorePageTabs.CUSTOM_FIELDS,
        }),
      },
    },
    states: {
      activeTab,
      isWhatsNewTab,
      isButtonDisabled: buttonState[activeTab] ?? false,
    },
    commands: { onDemoRequested, onDemoRequestFailed, onTourClicked },
  }
}
